import React, { useState } from "react";
import { usePhrase, useLang } from "@/contexts/LanguageContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, ShieldCheck, LogOut, KeyRound, X, CheckCircle2, Landmark, Building2, ShieldQuestion } from "lucide-react";

const demoAccounts = [
  { email: "borrower@bharatkosh.demo", label: "MSME Borrower", tone: "Complete Financial Passport, receive lender interest" },
  { email: "bankadmin@bharatkosh.demo", label: "Bank Administrator", tone: "Policies, opportunities & analytics" },
  { email: "rm@bharatkosh.demo", label: "Bank Relationship Manager", tone: "Express interest, sanction, disburse" },
  { email: "admin@bharatkosh.demo", label: "Platform Administrator", tone: "Schemes, users, audit logs" },
  { email: "facilitator@bharatkosh.demo", label: "Facilitator", tone: "Assist borrowers" },
  { email: "government@bharatkosh.demo", label: "Government Monitor", tone: "Aggregated district & scheme dashboards" },
];

// Five Indian MSME + finance scenes that rotate on every visit — balances gender,
// industry (textile, food, retail, engineering) and finance/banking imagery.
const HERO_ROTATION = [
  {
    src: "https://images.pexels.com/photos/31336015/pexels-photo-31336015.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900",
    caption: "Precision Engineering · Indore", caption_hi: "सटीक इंजीनियरिंग · इंदौर",
    alt: "Indian woman engineer operating heavy machinery at an MSME plant",
    heading_hi: <>एक बार तैयारी करें। <em className="italic text-[#F5B981] font-normal">उपयुक्त ऋणदाता</em> आपको खोजेंगे।</>,
    heading: <>Prepare once. <em className="italic text-[#F5B981] font-normal">Let suitable lenders</em> find you.</>,
  },
  {
    src: "https://images.pexels.com/photos/6949788/pexels-photo-6949788.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900",
    caption: "Spice & Food · Bhopal", caption_hi: "मसाले एवं खाद्य · भोपाल",
    alt: "Indian woman entrepreneur at her spice and grocery MSME",
    heading_hi: <><em className="italic text-[#F5B981] font-normal">खेत से वित्त तक,</em> एक पासपोर्ट में।</>,
    heading: <>From <em className="italic text-[#F5B981] font-normal">field to finance,</em> in one passport.</>,
  },
  {
    src: "https://images.pexels.com/photos/7580648/pexels-photo-7580648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900",
    caption: "Credit Committee · Central India", caption_hi: "क्रेडिट समिति · मध्य भारत",
    alt: "Indian banking committee reviewing an MSME loan application",
    heading_hi: <>जहाँ <em className="italic text-[#F5B981] font-normal">नीति मिलती है</em> एक होनहार उद्यमी से।</>,
    heading: <>Where <em className="italic text-[#F5B981] font-normal">policy meets</em> a promising promoter.</>,
  },
  {
    src: "https://images.pexels.com/photos/26861411/pexels-photo-26861411.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900",
    caption: "Retail & Kirana · Jabalpur", caption_hi: "खुदरा एवं किराना · जबलपुर",
    alt: "Indian small-shop owner at a neighbourhood kirana",
    heading_hi: <>हर <em className="italic text-[#F5B981] font-normal">दुकान, मंडी, कार्यशाला</em> — एक ऋण मार्ग।</>,
    heading: <>Every <em className="italic text-[#F5B981] font-normal">shop, mandi, workshop</em> — one credit rail.</>,
  },
  {
    src: "https://images.pexels.com/photos/31212954/pexels-photo-31212954.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900",
    caption: "Textile Cooperative · Gwalior", caption_hi: "वस्त्र सहकारी · ग्वालियर",
    alt: "Indian woman worker at a textile spinning mill",
    heading_hi: <>हथकरघा से <em className="italic text-[#F5B981] font-normal">कार्यशील पूंजी तक,</em> कम क्लिक में।</>,
    heading: <>Handloom to <em className="italic text-[#F5B981] font-normal">working capital,</em> in fewer clicks.</>,
  },
];

const TRUST_PARTNERS = [
  { name: "MP MSME", tag: "State Department", icon: Landmark },
  { name: "SIDBI", tag: "Refinance Partner", icon: Building2 },
  { name: "CGTMSE", tag: "Credit Guarantee", icon: ShieldQuestion },
];

export default function Login() {
  const pt = usePhrase();
  const { lang } = useLang();
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get("email") || "borrower@bharatkosh.demo");
  const [password, setPassword] = useState("Demo@123");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  // Pick one MSME scene per component mount — a page reload rotates naturally.
  // No useMemo needed; the computation is O(1) and the array literal is stable.
  const [hero] = useState(() => HERO_ROTATION[Math.floor(Math.random() * HERO_ROTATION.length)]);

  const handleSwitchAccount = async () => {
    await logout();
    window.location.assign("/login");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email, password);
      toast.success(localStorage.getItem("lang") === "hi" ? pt("साइन इन हो गया") : pt("Signed in"));
      const to = loc.state?.from || "/dashboard";
      nav(to, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  // --- Forgot password modal state ---
  const [fpOpen, setFpOpen] = useState(false);
  const [fpStep, setFpStep] = useState(1); // 1 = enter email, 2 = enter OTP + new password
  const [fpEmail, setFpEmail] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [fpNewPw, setFpNewPw] = useState("");
  const [fpConfirm, setFpConfirm] = useState("");
  const [fpErr, setFpErr] = useState("");
  const [fpBusy, setFpBusy] = useState(false);

  const openForgot = () => {
    setFpEmail(email || "");
    setFpStep(1); setFpOtp(""); setFpNewPw(""); setFpConfirm(""); setFpErr("");
    setFpOpen(true);
  };
  const closeForgot = () => setFpOpen(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setFpBusy(true); setFpErr("");
    try {
      await api.post("/auth/forgot-password/request", { email: fpEmail });
      toast.success(pt("One-time code sent. Demo OTP is 123456."));
      setFpStep(2);
    } catch (err) {
      setFpErr(formatApiError(err));
    } finally { setFpBusy(false); }
  };
  const submitReset = async (e) => {
    e.preventDefault();
    setFpErr("");
    if (fpNewPw.length < 8) { setFpErr("Password must be at least 8 characters."); return; }
    if (fpNewPw !== fpConfirm) { setFpErr("Passwords do not match."); return; }
    setFpBusy(true);
    try {
      await api.post("/auth/forgot-password/reset", { email: fpEmail, otp: fpOtp, new_password: fpNewPw });
      toast.success(pt("Password reset. Sign in with your new password."));
      setPassword(fpNewPw);
      setEmail(fpEmail);
      setFpOpen(false);
    } catch (err) {
      setFpErr(formatApiError(err));
    } finally { setFpBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[calc(100vh-4rem)]">
      {/* Photo panel */}
      <aside className="hidden lg:block lg:col-span-6 relative overflow-hidden">
        <img src={hero.src} alt={hero.alt} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 editorial-hero-scrim" />
        <div className="absolute inset-0 grain" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-white/85">
            <span className="rounded-full bg-black/25 backdrop-blur-md px-2.5 py-1">{pt("MP MSME Credit Exchange")}</span>
            <span data-testid="login-hero-caption" className="rounded-full bg-black/35 backdrop-blur-md px-2.5 py-1">{(lang === "hi" && hero.caption_hi) || hero.caption}</span>
          </div>
          <div>
            <h2 className="brand-heading text-4xl xl:text-5xl leading-[1.02] max-w-md" data-testid="login-hero-heading">
              {lang === "hi" ? hero.heading_hi : hero.heading}
            </h2>
            <p className="mt-5 text-white/80 max-w-md leading-relaxed">
              {pt("Enter with any of the six seeded demo roles to explore the full end-to-end flow — from Financial Passport to disbursement.")}
            </p>
          </div>
        </div>
      </aside>

      {/* Form panel */}
      <section className="col-span-1 lg:col-span-6 flex items-center justify-center px-5 py-10 sm:px-6 sm:py-16 lg:px-14" style={{ background: "var(--bk-ivory)" }}>
        <div className="w-full max-w-md">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--bk-navy)]/60">{pt("Log in")}</div>
          <h1 className="mt-3 brand-heading text-3xl sm:text-4xl text-[color:var(--bk-navy)] leading-[1.1]" data-testid="login-heading">
            {pt("Welcome back.")}
          </h1>
          <p className="mt-3 text-[color:var(--bk-navy)]/75 text-sm sm:text-base max-w-sm leading-relaxed">
            {pt("Sign in to access your Financial Passport, lender matches and loan applications.")}
          </p>

          {user && (
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="login-signed-in-banner">
              <div className="flex-1 text-sm text-amber-900">
                {lang === "hi" ? <>आप पहले से <strong>{user.email}</strong> के रूप में साइन इन हैं। किसी दूसरे खाते से साइन इन करने के लिए लॉग आउट करें।</> : <>You&apos;re already signed in as <strong>{user.email}</strong>. Log out to sign in with a different account.</>}
              </div>
              <button
                onClick={handleSwitchAccount}
                data-testid="login-signout-btn"
                className="inline-flex items-center gap-2 rounded-md bg-amber-900 hover:bg-amber-800 text-white px-4 py-2 text-xs font-semibold whitespace-nowrap"
              >
                <LogOut className="w-3.5 h-3.5" /> {pt("Log out & continue")}
              </button>
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-semibold text-[color:var(--bk-navy)]/70 uppercase tracking-widest">{pt("Email")}</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" required
                className="mt-2 w-full rounded-xl bg-white border border-[color:var(--bk-line)] px-4 py-3 focus:border-[color:var(--bk-navy)] focus:ring-2 focus:ring-[color:var(--bk-navy)]/10 outline-none transition-colors"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-[color:var(--bk-navy)]/70 uppercase tracking-widest">{pt("Password")}</label>
                <button
                  type="button"
                  onClick={openForgot}
                  data-testid="forgot-password-link"
                  className="text-xs font-semibold text-[color:var(--bk-saffron-2)] hover:text-[color:var(--bk-saffron)] underline underline-offset-4"
                >
                  {pt("Forgot password?")}
                </button>
              </div>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" required
                className="mt-2 w-full rounded-xl bg-white border border-[color:var(--bk-line)] px-4 py-3 focus:border-[color:var(--bk-navy)] focus:ring-2 focus:ring-[color:var(--bk-navy)]/10 outline-none transition-colors"
              />
            </div>
            {error && <div data-testid="login-error" className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>}
            <button
              type="submit" disabled={loading} data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white transition-colors disabled:opacity-60"
              style={{ background: "var(--bk-navy)" }}
            >
              {loading ? "Signing in…" : (<>{pt("Sign in")} <ArrowUpRight className="w-4 h-4" /></>)}
            </button>
            <div className="text-sm text-center text-[color:var(--bk-navy)]/70">
              {pt("New to Loan Bazaar?")}{" "}
              <Link data-testid="link-register" className="font-semibold text-[color:var(--bk-saffron-2)] hover:text-[color:var(--bk-saffron)] underline underline-offset-4" to="/register">
                {pt("Register")}
              </Link>
            </div>
          </form>

          <div className="mt-10 pt-8 border-t border-[color:var(--bk-line)]">
            <div className="flex items-center gap-2 text-xs text-[color:var(--bk-navy)]/70">
              <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--bk-emerald)]" />
              {pt("Demo mode — all accounts use password")} <code className="bg-white border border-[color:var(--bk-line)] px-1 rounded">Demo@123</code>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {demoAccounts.map((d) => (
                <button
                  key={d.email} type="button" data-testid={`demo-fill-${d.email}`}
                  onClick={() => { setEmail(d.email); setPassword("Demo@123"); }}
                  className="text-left rounded-lg border border-[color:var(--bk-line)] bg-white px-3 py-2.5 hover:border-[color:var(--bk-navy)]/40 transition-colors"
                >
                  <div className="text-[13px] font-semibold text-[color:var(--bk-navy)] leading-tight">{pt(d.label)}</div>
                  <div className="text-[11px] text-[color:var(--bk-navy)]/55 mt-0.5">{pt(d.tone)}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Trust row — institutional partners */}
          <div className="mt-8" data-testid="login-trust-row">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--bk-navy)]/50 text-center">{pt("Backed by institutional partners")}</div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {TRUST_PARTNERS.map((p) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.name}
                    data-testid={`trust-${p.name.toLowerCase().replaceAll(" ","-")}`}
                    className="flex items-center gap-2 rounded-lg border border-[color:var(--bk-line)] bg-white/60 px-3 py-2.5"
                  >
                    <div className="w-7 h-7 rounded-md bg-[color:var(--bk-navy)] text-white grid place-items-center shrink-0">
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold text-[color:var(--bk-navy)] leading-tight truncate">{p.name}</div>
                      <div className="text-[9px] uppercase tracking-widest text-[color:var(--bk-navy)]/55 truncate">{pt(p.tag)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Forgot password modal */}
      {fpOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-[color:var(--bk-navy)]/40 backdrop-blur-sm"
          data-testid="forgot-password-modal"
          onClick={(e) => { if (e.target === e.currentTarget) closeForgot(); }}
        >
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-[color:var(--bk-line)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[color:var(--bk-line)]">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[color:var(--bk-navy)] text-white grid place-items-center">
                  <KeyRound className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-semibold text-[color:var(--bk-navy)]">{pt("Reset password")}</div>
                  <div className="text-[11px] text-[color:var(--bk-navy)]/60">{lang === "hi" ? `चरण ${fpStep} / 2 · डेमो OTP प्रक्रिया` : `Step ${fpStep} of 2 · demo OTP flow`}</div>
                </div>
              </div>
              <button onClick={closeForgot} data-testid="fp-close" className="text-[color:var(--bk-navy)]/60 hover:text-[color:var(--bk-navy)]">
                <X className="w-4 h-4" />
              </button>
            </div>

            {fpStep === 1 ? (
              <form onSubmit={requestOtp} className="p-5 space-y-4" data-testid="fp-step-1">
                <p className="text-sm text-[color:var(--bk-navy)]/70">
                  {pt("Enter the email tied to your Loan Bazaar account. We'll send a one-time code to your registered mobile.")}
                </p>
                <div>
                  <label className="text-xs font-semibold text-[color:var(--bk-navy)]/70 uppercase tracking-widest">{pt("Email")}</label>
                  <input
                    type="email" required value={fpEmail} onChange={(e) => setFpEmail(e.target.value)}
                    data-testid="fp-email"
                    className="mt-2 w-full rounded-lg bg-white border border-[color:var(--bk-line)] px-3 py-2.5 focus:border-[color:var(--bk-navy)] focus:ring-2 focus:ring-[color:var(--bk-navy)]/10 outline-none"
                  />
                </div>
                {fpErr && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="fp-error">{fpErr}</div>}
                <button
                  type="submit" disabled={fpBusy} data-testid="fp-send-otp"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[color:var(--bk-navy)] hover:bg-[color:var(--bk-navy)]/90 text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
                >
                  {fpBusy ? pt("Sending…") : pt("Send one-time code")}
                </button>
              </form>
            ) : (
              <form onSubmit={submitReset} className="p-5 space-y-4" data-testid="fp-step-2">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>{lang === "hi" ? <>डेमो कोड भेजा गया। आगे बढ़ने के लिए <strong>123456</strong> का उपयोग करें।</> : <>Demo code sent. Use <strong>123456</strong> to continue.</>}</div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[color:var(--bk-navy)]/70 uppercase tracking-widest">{pt("One-time code")}</label>
                  <input
                    type="text" required maxLength={6} value={fpOtp} onChange={(e) => setFpOtp(e.target.value.replace(/\D/g,""))}
                    data-testid="fp-otp"
                    className="mt-2 w-full rounded-lg bg-white border border-[color:var(--bk-line)] px-3 py-2.5 tracking-widest text-center font-semibold focus:border-[color:var(--bk-navy)] focus:ring-2 focus:ring-[color:var(--bk-navy)]/10 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[color:var(--bk-navy)]/70 uppercase tracking-widest">{pt("New password")}</label>
                  <input
                    type="password" required minLength={8} value={fpNewPw} onChange={(e) => setFpNewPw(e.target.value)}
                    data-testid="fp-new"
                    className="mt-2 w-full rounded-lg bg-white border border-[color:var(--bk-line)] px-3 py-2.5 focus:border-[color:var(--bk-navy)] focus:ring-2 focus:ring-[color:var(--bk-navy)]/10 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[color:var(--bk-navy)]/70 uppercase tracking-widest">{pt("Confirm password")}</label>
                  <input
                    type="password" required value={fpConfirm} onChange={(e) => setFpConfirm(e.target.value)}
                    data-testid="fp-confirm"
                    className="mt-2 w-full rounded-lg bg-white border border-[color:var(--bk-line)] px-3 py-2.5 focus:border-[color:var(--bk-navy)] focus:ring-2 focus:ring-[color:var(--bk-navy)]/10 outline-none"
                  />
                </div>
                {fpErr && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="fp-error">{fpErr}</div>}
                <div className="flex gap-2">
                  <button
                    type="button" onClick={() => setFpStep(1)} data-testid="fp-back"
                    className="rounded-lg border border-[color:var(--bk-line)] text-[color:var(--bk-navy)] bg-white px-3 py-2.5 text-sm font-semibold"
                  >
                    {pt("Back")}
                  </button>
                  <button
                    type="submit" disabled={fpBusy} data-testid="fp-reset-submit"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-[color:var(--bk-navy)] hover:bg-[color:var(--bk-navy)]/90 text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
                  >
                    {fpBusy ? pt("Resetting…") : pt("Reset password")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
